import React, { useMemo } from 'react';
import { X, Send } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Label,
} from 'recharts';
import './CampaignAnalyticsModal.css';

const TEAL  = '#1D9E75';
const BLUE  = '#185FA5';
const RED   = '#E24B4A';
const AMBER = '#EF9F27';

const CamTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="cam-tt">
      {label && <p className="cam-tt-label">{label}</p>}
      {payload.map((p, i) => (
        <div key={i} className="cam-tt-row">
          <span className="cam-tt-dot" style={{ background: p.fill || p.color }} />
          <span>{p.name}: <strong>{(p.value ?? 0).toLocaleString()}</strong></span>
        </div>
      ))}
    </div>
  );
};

const CampaignAnalyticsModal = ({ data, loading, onClose }) => {
  const campaigns    = data?.campaigns   || {};
  const messages     = data?.messages    || {};
  const topCampaigns = data?.topCampaigns || [];

  const campaignPieData = useMemo(() => [
    { name: 'Sent',  value: campaigns.sent  || 0, color: TEAL  },
    { name: 'Draft', value: campaigns.draft || 0, color: AMBER },
  ].filter(d => d.value > 0), [campaigns]);

  const campaignBarItems = useMemo(() => [
    { name: 'Sent',              value: campaigns.sent          || 0, color: TEAL  },
    { name: 'Draft',             value: campaigns.draft         || 0, color: AMBER },
    { name: 'Created This Month', value: campaigns.thisMonth     || 0, color: BLUE  },
    { name: 'Sent This Month',   value: campaigns.sentThisMonth || 0, color: BLUE  },
  ], [campaigns]);

  const campaignBarSum = campaignBarItems.reduce((a, b) => a + b.value, 0);
  const campaignTotal  = campaigns.total || 0;

  const monthlyData = useMemo(() =>
    (data?.monthlyTrend || []).map(m => ({
      name:    m.monthLabel.split(' ')[0],
      Created: m.campaignsCreated,
      Sent:    m.campaignsSent,
    })),
  [data]);

  const hasTrend = monthlyData.some(m => m.Created > 0 || m.Sent > 0);

  return (
    <div className="cam-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cam-modal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="cam-hdr">
          <div className="cam-hdr-left">
            <div className="cam-hdr-badge"><Send size={16} /></div>
            <div>
              <h2 className="cam-hdr-title">Campaign Analytics</h2>
              <p className="cam-hdr-sub">Overview of your campaign performance</p>
            </div>
          </div>
          <button className="cam-hdr-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        {/* ── Body ── */}
        {loading ? (
          <div className="cam-loading">
            <div className="cam-spin" />
            <p>Loading analytics…</p>
          </div>
        ) : (
          <div className="cam-body">

            {/* Section 1 — Campaign Overview */}
            <div className="cam-section">
              <p className="cam-section-label">Campaign Overview</p>
              <div className="cam-stat-grid">
                {[
                  { label: 'Total',              value: campaigns.total         || 0, accent: null  },
                  { label: 'Sent',               value: campaigns.sent          || 0, accent: TEAL  },
                  { label: 'Draft',              value: campaigns.draft         || 0, accent: null  },
                  { label: 'Created This Month', value: campaigns.thisMonth     || 0, accent: BLUE  },
                  { label: 'Sent This Month',    value: campaigns.sentThisMonth || 0, accent: BLUE  },
                ].map(s => (
                  <div key={s.label} className="cam-stat-card">
                    <span className="cam-stat-val" style={s.accent ? { color: s.accent } : {}}>{s.value}</span>
                    <span className="cam-stat-lbl">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 2 — Message Performance */}
            <div className="cam-section">
              <p className="cam-section-label">Message Performance</p>
              <div className="cam-perf-grid">
                {[
                  { label: 'Delivery Rate', value: `${messages.deliveryRate ?? 0}%`, color: TEAL },
                  { label: 'Read Rate',     value: `${messages.readRate    ?? 0}%`, color: BLUE },
                  { label: 'Failure Rate',  value: `${messages.failureRate ?? 0}%`, color: RED  },
                ].map(r => (
                  <div key={r.label} className="cam-perf-card">
                    <span className="cam-perf-val" style={{ color: r.color }}>{r.value}</span>
                    <span className="cam-perf-lbl">{r.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3 — Campaign Breakdown */}
            <div className="cam-section">
              <p className="cam-section-label">Campaign Breakdown</p>
              <div className="cam-breakdown-row">

                {/* Donut */}
                <div className="cam-card cam-donut-card">
                  <div className="cam-donut-wrap">
                    <ResponsiveContainer width="100%" height={190}>
                      <PieChart>
                        <Pie
                          data={campaignPieData.length ? campaignPieData : [{ name: 'No data', value: 1, color: '#e2e8f0' }]}
                          cx="50%" cy="50%"
                          innerRadius={52} outerRadius={78}
                          paddingAngle={campaignPieData.length > 1 ? 2 : 0}
                          dataKey="value"
                          stroke="none"
                          startAngle={90}
                          endAngle={-270}
                        >
                          {(campaignPieData.length ? campaignPieData : [{ color: '#e2e8f0' }]).map((d, i) => (
                            <Cell key={i} fill={d.color} />
                          ))}
                          <Label
                            content={({ viewBox }) => {
                              const { cx, cy } = viewBox;
                              return (
                                <g>
                                  <text x={cx} y={cy - 5} textAnchor="middle" dominantBaseline="middle"
                                    style={{ fontSize: 20, fontWeight: 500, fill: 'var(--cam-text)', fontFamily: 'inherit' }}>
                                    {campaignTotal.toLocaleString()}
                                  </text>
                                  <text x={cx} y={cy + 16} textAnchor="middle" dominantBaseline="middle"
                                    style={{ fontSize: 10, fill: 'var(--cam-muted)', fontFamily: 'inherit' }}>
                                    total
                                  </text>
                                </g>
                              );
                            }}
                          />
                        </Pie>
                        <Tooltip content={<CamTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="cam-donut-legend">
                    {[
                      { label: 'Sent',  color: TEAL  },
                      { label: 'Draft', color: AMBER },
                    ].map(l => (
                      <span key={l.label} className="cam-legend-item">
                        <span className="cam-legend-dot" style={{ background: l.color }} />
                        {l.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Horizontal bars */}
                <div className="cam-card cam-hbar-card">
                  <p className="cam-card-title">Campaign Counts</p>
                  <div className="cam-hbars">
                    {campaignBarItems.map(item => {
                      const pct = campaignBarSum > 0 ? Math.round((item.value / campaignBarSum) * 100) : 0;
                      return (
                        <div key={item.name} className="cam-hbar-row">
                          <span className="cam-hbar-lbl">{item.name}</span>
                          <div className="cam-hbar-track">
                            <div className="cam-hbar-fill" style={{ width: `${pct}%`, background: item.color }} />
                          </div>
                          <span className="cam-hbar-val">{item.value.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </div>
            </div>

            {/* Section 4 — Monthly Campaign Trend */}
            {hasTrend && (
              <div className="cam-section">
                <p className="cam-section-label">Monthly Campaign Trend</p>
                <div className="cam-card cam-trend-card">
                  <div className="cam-chart-legend">
                    <span className="cam-legend-item"><span className="cam-legend-dot" style={{ background: BLUE }} />Created</span>
                    <span className="cam-legend-item"><span className="cam-legend-dot" style={{ background: TEAL }} />Sent</span>
                  </div>
                  <ResponsiveContainer width="100%" height={175}>
                    <BarChart data={monthlyData} barSize={14} barGap={3} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--cam-grid)" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--cam-muted)' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: 'var(--cam-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<CamTooltip />} cursor={{ fill: 'var(--cam-hover)' }} />
                      <Bar dataKey="Created" fill={BLUE} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Sent"    fill={TEAL} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Section 5 — Top Campaigns */}
            {topCampaigns.length > 0 && (
              <div className="cam-section">
                <p className="cam-section-label">Top Campaigns</p>
                <div className="cam-table-wrap">
                  <table className="cam-table">
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th>Template</th>
                        <th>Recip.</th>
                        <th style={{ color: TEAL }}>Del.</th>
                        <th style={{ color: BLUE }}>Seen</th>
                        <th>Del. %</th>
                        <th>Read %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCampaigns.map(c => (
                        <tr key={c.campaignId}>
                          <td>
                            <p className="cam-camp-name">{c.campaignName}</p>
                            <span className={`cam-type-pill ${c.templateCategory === 'utility' ? 'cam-pill-teal' : 'cam-pill-amber'}`}>
                              {c.templateCategory}
                            </span>
                          </td>
                          <td className="cam-mono">{(c.templateName || '').replace(/_/g, ' ')}</td>
                          <td>{c.totalRecipients}</td>
                          <td style={{ color: TEAL, fontWeight: 500 }}>{c.deliveredCount}</td>
                          <td style={{ color: BLUE, fontWeight: 500 }}>{c.seenCount}</td>
                          <td>
                            <div className="cam-mini-bar">
                              <div className="cam-mini-track">
                                <div className="cam-mini-fill" style={{ width: `${Math.min(c.deliveryRate, 100)}%`, background: TEAL }} />
                              </div>
                              <span className="cam-mini-pct">{c.deliveryRate}%</span>
                            </div>
                          </td>
                          <td>
                            <div className="cam-mini-bar">
                              <div className="cam-mini-track">
                                <div className="cam-mini-fill" style={{ width: `${Math.min(c.readRate, 100)}%`, background: BLUE }} />
                              </div>
                              <span className="cam-mini-pct">{c.readRate}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignAnalyticsModal;

